import React, { useEffect, useMemo, useState } from 'react';
import { collection, query, where, getDocs, doc, getDoc } from 'firebase/firestore';
import {
  FaClipboardList,
  FaChartPie,
  FaBalanceScale,
  FaFileInvoiceDollar,
  FaHospital,
  FaEdit,
} from 'react-icons/fa';
import { db } from '../../config/firebaseConfig';
import InfoCardModal, {
  InfoItem,
  InfoSection,
  InfoGrid,
  StatGrid,
  TagList,
  display,
  formatDate,
  daysHint,
  initialsFrom,
  isEmptyValue,
} from '../../components/InfoCard/InfoCardModal';

const ProjectCardModal = ({ project, onClose, onEdit }) => {
  const [loading, setLoading] = useState(true);
  const [counts, setCounts] = useState({ patients: 0, visits: 0 });
  // Liste verisindeki alanlar ProjectsManagementPage içindeki formatValue'dan
  // geçiyor; bu da employees dizisini metne çeviriyor. Bu yüzden kart, ham
  // dokümanı Firestore'dan yeniden okur (WorkersModal, BudgetModal ve
  // EmployeeFileUploadModal da aynı yolu izliyor).
  const [rawEmployees, setRawEmployees] = useState(null);

  useEffect(() => {
    if (!project?.id) return;
    let cancelled = false;

    const fetchCounts = async () => {
      setLoading(true);
      try {
        const [projectSnap, patientsSnap, visitsSnap] = await Promise.all([
          getDoc(doc(db, 'Projects', project.id)),
          getDocs(query(collection(db, 'Patients'), where('projectId', '==', project.id))),
          getDocs(query(collection(db, 'Visits'), where('projectId', '==', project.id))),
        ]);
        if (cancelled) return;
        const projectData = projectSnap.exists() ? projectSnap.data() : {};
        setRawEmployees(Array.isArray(projectData.employees) ? projectData.employees : []);
        setCounts({ patients: patientsSnap.docs.length, visits: visitsSnap.docs.length });
      } catch (error) {
        console.error('Error fetching project card data:', error);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    fetchCounts();
    return () => {
      cancelled = true;
    };
  }, [project?.id]);

  // Proje dokümanındaki employees dizisi merkez/rol başına ayrı kayıt tutuyor
  const { activeWorkerCount, centerNames } = useMemo(() => {
    // Ham doküman gelene kadar prop'a düş; prop dizi değilse (formatValue metne
    // çevirmişse) boş kabul et ki forEach patlamasın.
    const employees =
      rawEmployees ?? (Array.isArray(project?.employees) ? project.employees : []);
    const activeIds = new Set();
    const centers = new Set();

    employees.forEach((emp) => {
      if (emp.status !== 'passive') {
        if (emp.id) activeIds.add(emp.id);
        if (emp.centerName) centers.add(emp.centerName);
      }
    });

    if (project?.center) centers.add(project.center);

    return { activeWorkerCount: activeIds.size, centerNames: [...centers] };
  }, [rawEmployees, project]);

  if (!project) return null;

  const projectStatus = project.projectStatus || 'Aktif';
  const isActive = projectStatus === 'Aktif';

  return (
    <InfoCardModal
      ariaLabel="Proje Kartı"
      title={project.titleShort || 'Proje'}
      subtitle={display(project.titleLong)}
      initials={initialsFrom(project.titleShort, 'PR')}
      status={{
        label: isActive ? 'Aktif' : 'Pasif',
        tone: isActive ? 'active' : 'inactive',
      }}
      chips={[
        project.customer,
        project.projectPhase,
        project.isFinished ? 'Bitti' : null,
      ].filter(Boolean)}
      onClose={onClose}
      onEdit={onEdit}
      editIcon={<FaEdit />}
    >
      <InfoSection title="Özet" icon={FaChartPie}>
        <StatGrid
          loading={loading}
          stats={[
            { label: 'Aktif Çalışan', value: activeWorkerCount },
            { label: 'Merkez', value: centerNames.length },
            { label: 'Hasta', value: counts.patients },
            { label: 'Ziyaret', value: counts.visits },
          ]}
        />
      </InfoSection>

      <InfoSection title="Proje Bilgileri" icon={FaClipboardList}>
        <InfoGrid>
          <InfoItem label="Proje Kodu" value={display(project.titleShort)} copyable />
          <InfoItem label="Protokol Numarası" value={display(project.protocolNumber)} copyable />
          <InfoItem label="Müşteri" value={display(project.customer)} />
          <InfoItem label="Proje Durumu" value={projectStatus} />
          <InfoItem label="Proje Aşaması" value={display(project.projectPhase)} />
          <InfoItem label="Proje Departmanı" value={display(project.projectDepartment)} />
          <InfoItem label="Proje Tarihi" value={formatDate(project.projectDate)} />
          <InfoItem
            label="Proje Bitiş Tarihi"
            value={formatDate(project.projectDueDate)}
            hint={daysHint(project.projectDueDate)}
          />
          <InfoItem label="Proje Merkezi Tipi" value={display(project.projectCentersType)} />
          <InfoItem
            label="Koordinatör Akademisyen"
            value={display(project.coordinatorAcademician)}
          />
          <InfoItem label="Proje Adı" value={display(project.titleLong)} wide />
        </InfoGrid>
      </InfoSection>

      <InfoSection title="Onay ve Etik Kurul" icon={FaBalanceScale}>
        <InfoGrid>
          <InfoItem label="Etik Kurul Tarihi" value={formatDate(project.ethicsCommitteeDate)} />
          <InfoItem label="Etik Kurul Numarası" value={display(project.ethicsCommitteeNumber)} />
          <InfoItem label="Etik Kurul Yeri" value={display(project.ethicsCommitteePlace)} />
          <InfoItem label="SB İlk Onay Tarihi" value={formatDate(project.sbFirstApprovalDate)} />
          <InfoItem label="SB İlk Onay Numarası" value={display(project.sbFirstApprovalNumber)} />
          <InfoItem label="SB Proje Numarası" value={display(project.sbProjectNumber)} copyable />
        </InfoGrid>
      </InfoSection>

      <InfoSection title="Finans Bilgileri" icon={FaFileInvoiceDollar}>
        <InfoGrid>
          <InfoItem label="İlk Fatura Tarihi" value={formatDate(project.firstInvoiceDate)} />
          <InfoItem label="Ödeme Sıklığı" value={display(project.paymentFrequency)} />
          <InfoItem
            label="Kur Sabitlemesi"
            value={project.isCurrencyFixed ? 'Evet' : 'Hayır'}
          />
          <InfoItem
            label="Sabit Kur"
            value={
              project.isCurrencyFixed && !isEmptyValue(project.fixedCurrencyRate)
                ? display(project.fixedCurrencyRate)
                : '—'
            }
          />
        </InfoGrid>
      </InfoSection>

      <InfoSection title={`Merkezler (${centerNames.length})`} icon={FaHospital}>
        <TagList items={centerNames} emptyText="Projeye bağlı merkez kaydı bulunmuyor." />
      </InfoSection>
    </InfoCardModal>
  );
};

export default ProjectCardModal;
